import { getImageUrl } from '@routellers/utils/dist/image-url';
import { Alert, Anchor, Box, Image, Stack, Text } from '@mantine/core';
import type {
    FieldEvent,
    FieldTarget,
} from '@routellers/types/dist/openapi/city-player/city-field';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { Section } from '../components/editor';
type Props = {
    target?: FieldTarget;
    cityId?: number;
    title?: string;
    collapsed?: boolean;
    hideEmpty?: boolean;
};

export function FieldHistoryPanel(props: Props) {
    return (
        <FieldHistoryContent
            key={`${props.target?.kind}:${props.target?.id}:${props.cityId}`}
            {...props}
        />
    );
}

function FieldHistoryContent({
    target,
    cityId,
    title = 'История работы в поле',
    collapsed = false,
    hideEmpty = true,
}: Props) {
    const [events, setEvents] = useState<FieldEvent[]>([]);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let live = true;
        setError(false);
        const request = target
            ? api.admins.fieldHistory(target)
            : cityId
              ? api.admins.fieldJournal({ cityId })
              : null;
        void request
            ?.then(({ data }) => {
                if (live) {
                    setEvents(data);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (live) {
                    setError(true);
                    setLoading(false);
                }
            });
        return () => {
            live = false;
        };
    }, [target?.kind, target?.id, cityId]);
    const labels: Record<string, string> = {
        fact: 'Исправлен факт',
        review: 'Проверка на месте',
        patch: 'Исправлены данные',
        note: 'Заметка',
        exclude: 'Исключено из маршрута',
        photo: 'Добавлено фото',
        cover: 'Выбрана обложка',
        removePhoto: 'Удалено фото',
        collection: 'Изменена подборка',
        undo: 'Отмена изменения',
    };
    if (hideEmpty && !error && !events.length) return null;
    const content = (
        <Stack gap="sm">
            {!error && !events.length && (
                <Text size="sm" c="dimmed">
                    {loading ? 'Загрузка истории…' : 'Изменений пока нет'}
                </Text>
            )}
            {error && <Alert color="red">Не удалось загрузить историю</Alert>}
            {events.map((e) => (
                <Stack
                    key={e.id}
                    gap={4}
                    style={{
                        borderBottom:
                            '1px solid var(--mantine-color-default-border)',
                        paddingBottom: 12,
                    }}
                >
                    <Text fw={600}>
                        {labels[e.action] || e.action}
                        {e.undone ? ' · отменено' : ''}
                        {!target || target.kind === 'route'
                            ? ` · ${e.title}`
                            : ''}
                    </Text>
                    {['attraction', 'establishment', 'route', 'stop'].includes(
                        e.kind,
                    ) && (
                        <Anchor
                            size="sm"
                            href={`${e.kind === 'attraction' ? '/attractions' : e.kind === 'establishment' ? '/establishments' : '/routes'}?edit=${e.kind === 'stop' ? e.routeId : e.targetId}`}
                        >
                            Открыть {e.kind === 'stop' ? 'маршрут' : 'карточку'}{' '}
                            ↗
                        </Anchor>
                    )}
                    <Text size="xs" c="dimmed">
                        {new Date(e.createdAt).toLocaleString()}
                        {e.routeId ? ` · маршрут #${e.routeId}` : ''}
                    </Text>
                    {!!e.body && (
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                            {e.body}
                        </Text>
                    )}
                    {e.photoUrl && (
                        <Anchor
                            href={getImageUrl(e.photoUrl) || e.photoUrl}
                            target="_blank"
                        >
                            <Image
                                src={getImageUrl(e.photoUrl) || e.photoUrl}
                                h={160}
                                w={220}
                                fit="contain"
                            />
                        </Anchor>
                    )}
                </Stack>
            ))}
        </Stack>
    );
    if (collapsed)
        return (
            <Box
                component="details"
                style={{
                    border: '1px solid var(--mantine-color-default-border)',
                    borderRadius: 12,
                    padding: 16,
                }}
            >
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                    {title} · {events.length}
                </summary>
                <Box mt="md">{content}</Box>
            </Box>
        );
    return (
        <Section
            boxed
            title={title}
            description="Решения, заметки и правки с телефона."
        >
            {content}
        </Section>
    );
}
